import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useRole } from '../hooks/useRole';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { TableSkeleton } from '../components/ui/Skeleton';
import toast from 'react-hot-toast';
import * as Lu from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';
import { fmtDate, cn } from '../lib/utils';

const ROLE_STYLES = {
  owner: { variant: 'danger', label: 'Owner' },
  executive: { variant: 'warning', label: 'Executive' },
  teacher: { variant: 'primary', label: 'Teacher' },
};

function generatePassword() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

function DisableModal({ profile, onClose, onConfirm, saving }) {
  const isDisabled = profile?.is_disabled;
  return (
    <div className="space-y-4">
      <div className={cn('rounded-xl p-4 border flex items-start gap-3', isDisabled ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100')}>
        <SafeIcon icon={isDisabled ? Lu.LuShieldCheck : Lu.LuShieldOff} size={16} className={isDisabled ? 'text-emerald-600 shrink-0 mt-0.5' : 'text-amber-600 shrink-0 mt-0.5'} />
        <div>
          <p className={cn('text-[10px] font-black uppercase tracking-widest', isDisabled ? 'text-emerald-700' : 'text-amber-700')}>
            {isDisabled ? 'Reinstate Account' : 'Temporarily Disable Account'}
          </p>
          <p className="text-[10px] text-slate-600 leading-relaxed mt-1">
            {isDisabled
              ? `This will restore login access for ${profile.first_name || profile.email}.`
              : `This will prevent ${profile.first_name || profile.email} from logging in. Their data is preserved.`}
          </p>
        </div>
      </div>
      <div className="flex gap-3">
        <Button variant="ghost" onClick={onClose} className="flex-1">Cancel</Button>
        <Button
          onClick={onConfirm}
          disabled={saving}
          variant={isDisabled ? 'primary' : 'secondary'}
          className={cn('flex-[2] font-black uppercase text-[10px] tracking-widest', !isDisabled && 'border-amber-400 text-amber-700 hover:bg-amber-50')}
        >
          {saving ? 'Saving...' : isDisabled ? 'Reinstate Account' : 'Disable Account'}
        </Button>
      </div>
    </div>
  );
}

export default function UserManagement() {
  const { isOwner, loading: roleLoading, profile: ownerProfile } = useRole();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [disableModal, setDisableModal] = useState({ open: false, profile: null });
  const [disableSaving, setDisableSaving] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', mobile_number: '',
    teacher_nickname: '', role: 'teacher',
  });

  useEffect(() => {
    if (!roleLoading) {
      if (!isOwner) { navigate('/'); return; }
      fetchAll();
    }
  }, [roleLoading, isOwner]);

  const fetchAll = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      // Order by email as a safe fallback — created_at may not exist on older schemas
      const { data: profilesData, error: profilesErr } = await supabase
        .from('profiles')
        .select('*')
        .order('email', { ascending: true });

      if (profilesErr) {
        console.error('Profiles fetch error:', profilesErr);
        setFetchError(`Profiles error: ${profilesErr.message}`);
      }

      const { data: logsData, error: logsErr } = await supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);

      if (logsErr) console.error('Audit logs fetch error:', logsErr);

      const { data: pendingData, error: pendingErr } = await supabase
        .from('pending_invites')
        .select('*')
        .eq('claimed', false)
        .order('created_at', { ascending: false });

      if (pendingErr) console.error('Pending invites fetch error:', pendingErr);

      const fetchedProfiles = profilesData || [];
      console.log(`[UserManagement] Fetched ${fetchedProfiles.length} profiles`, fetchedProfiles);

      setProfiles(fetchedProfiles);
      setAuditLogs(logsData || []);
      setPendingInvites(pendingData || []);
    } catch (e) {
      console.error('fetchAll error:', e);
      toast.error('Failed to load user data');
      setFetchError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    resetForm();
    setGeneratedPassword(generatePassword());
    setModalOpen(true);
  };

  const handleCreateTeacher = async (e) => {
    e.preventDefault();
    if (!form.email || !form.first_name) return toast.error('Name and email are required');
    setSaving(true);
    try {
      const { data: existing } = await supabase
        .from('profiles').select('id,email').eq('email', form.email).maybeSingle();
      if (existing) { toast.error('An account with this email already exists'); setSaving(false); return; }

      const { data: existingPending } = await supabase
        .from('pending_invites').select('id').eq('email', form.email).eq('claimed', false).maybeSingle();
      if (existingPending) { toast.error('A pending invite already exists for this email'); setSaving(false); return; }

      const { error } = await supabase.from('pending_invites').insert([{
        email: form.email, first_name: form.first_name, last_name: form.last_name,
        mobile_number: form.mobile_number, teacher_nickname: form.teacher_nickname,
        role: form.role, temp_password: generatedPassword, created_by: currentUser.id,
      }]);
      if (error) throw error;

      toast.success(`Invite created for ${form.first_name}!`, { duration: 6000 });
      setModalOpen(false);
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePending = async (invite) => {
    if (!confirm(`Cancel invite for ${invite.email}?`)) return;
    const l = toast.loading('Removing invite...');
    try {
      const { error } = await supabase.from('pending_invites').delete().eq('id', invite.id);
      if (error) throw error;
      toast.success('Invite cancelled', { id: l });
      fetchAll();
    } catch (err) {
      toast.error(err.message, { id: l });
    }
  };

  const handleUpdateRole = async (profileId, newRole) => {
    const l = toast.loading('Updating role...');
    try {
      const { error } = await supabase.from('profiles').update({ role: newRole }).eq('id', profileId);
      if (error) throw error;
      toast.success('Role updated', { id: l });
      fetchAll();
    } catch (err) {
      toast.error(err.message, { id: l });
    }
  };

  const handleDeleteProfile = async (profile) => {
    if (!confirm(`Permanently delete account for ${profile.first_name || profile.email}?\n\nThis cannot be undone.`)) return;
    const l = toast.loading('Deleting profile...');
    try {
      const { error } = await supabase.from('profiles').delete().eq('id', profile.id);
      if (error) throw error;
      toast.success('Profile permanently deleted', { id: l });
      fetchAll();
    } catch (err) {
      toast.error(err.message, { id: l });
    }
  };

  const handleToggleDisable = async () => {
    if (!disableModal.profile) return;
    setDisableSaving(true);
    const isCurrentlyDisabled = disableModal.profile.is_disabled;
    const l = toast.loading(isCurrentlyDisabled ? 'Reinstating account...' : 'Disabling account...');
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_disabled: !isCurrentlyDisabled })
        .eq('id', disableModal.profile.id);
      if (error) throw error;
      toast.success(
        isCurrentlyDisabled ? 'Account reinstated successfully.' : 'Account disabled.',
        { id: l }
      );
      setDisableModal({ open: false, profile: null });
      fetchAll();
    } catch (err) {
      toast.error(err.message, { id: l });
    } finally {
      setDisableSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied!'));
  };

  const resetForm = () => setForm({
    first_name: '', last_name: '', email: '', mobile_number: '', teacher_nickname: '', role: 'teacher',
  });

  const getCreatorName = (createdById) => {
    if (!createdById) return null;
    const creator = profiles.find(p => p.id === createdById);
    if (!creator) return null;
    return creator.teacher_nickname || `${creator.first_name || ''} ${creator.last_name || ''}`.trim() || creator.email;
  };

  const activeProfiles = profiles.filter(p =>
    `${p.first_name || ''} ${p.last_name || ''} ${p.email || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  const filteredLogs = auditLogs.filter(l =>
    (l.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = profiles.length;
  const disabledCount = profiles.filter(p => p.is_disabled).length;
  const ownerCount = profiles.filter(p => p.role === 'owner').length;
  const teacherCount = profiles.filter(p => p.role === 'teacher').length;

  if (roleLoading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
    </div>
  );

  if (!isOwner) return null;

  return (
    <div className="max-w-6xl mx-auto space-y-5 pb-24 px-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black uppercase tracking-tighter dark:text-white">User Management</h1>
          <p className="text-slate-400 text-xs font-bold uppercase mt-0.5">Owner-only · Manage staff accounts & audit logs</p>
        </div>
        <Button onClick={openModal} className="flex items-center gap-2">
          <SafeIcon icon={Lu.LuUserPlus} size={16} />
          <span className="hidden sm:inline font-black uppercase text-[10px] tracking-widest">New Staff</span>
        </Button>
      </div>

      {/* Fetch error banner */}
      {fetchError && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <SafeIcon icon={Lu.LuAlertCircle} size={14} className="text-rose-600 shrink-0" />
          <p className="text-[10px] font-black uppercase text-rose-700 tracking-widest flex-1">{fetchError}</p>
          <button onClick={fetchAll} className="text-[9px] font-black uppercase text-rose-600 hover:underline">Retry</button>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Profiles', value: activeCount, icon: Lu.LuUsers, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Owners', value: ownerCount, icon: Lu.LuShieldCheck, color: 'text-rose-600', bg: 'bg-rose-50' },
          { label: 'Teachers', value: teacherCount, icon: Lu.LuGraduationCap, color: 'text-primary-600', bg: 'bg-primary-50' },
          { label: 'Pending Invites', value: pendingInvites.length, icon: Lu.LuMailCheck, color: 'text-amber-600', bg: 'bg-amber-50' },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white dark:bg-slate-900 rounded-xl border shadow-sm p-4">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${s.bg} ${s.color}`}>
              <SafeIcon icon={s.icon} size={16} />
            </div>
            <p className="text-[9px] font-bold uppercase text-slate-400 mb-0.5">{s.label}</p>
            <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
          </motion.div>
        ))}
      </div>

      {disabledCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <SafeIcon icon={Lu.LuShieldOff} size={14} className="text-amber-600 shrink-0" />
          <p className="text-[10px] font-black uppercase text-amber-700 tracking-widest">
            {disabledCount} account{disabledCount > 1 ? 's' : ''} currently disabled
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl w-fit">
        {[
          { key: 'users', label: `Staff Accounts (${activeCount})`, icon: Lu.LuUsers },
          { key: 'pending', label: `Pending (${pendingInvites.length})`, icon: Lu.LuClock },
          { key: 'audit', label: 'Login Audit', icon: Lu.LuActivity },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={cn('flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all',
              activeTab === tab.key ? 'bg-white dark:bg-slate-900 text-primary-600 shadow-sm' : 'text-slate-400 hover:text-slate-600')}>
            <SafeIcon icon={tab.icon} size={13} />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <SafeIcon icon={Lu.LuSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
        <input
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 bg-white dark:bg-slate-900 rounded-xl border text-sm outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {/* ── Users Tab ── */}
      {activeTab === 'users' && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-6 py-4 border-b bg-slate-50 dark:bg-slate-800">
            <SafeIcon icon={Lu.LuUsers} size={14} className="text-primary-600" />
            <h2 className="text-[10px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-300">
              Staff Accounts — {activeProfiles.length}
            </h2>
            <button onClick={fetchAll}
              className="ml-auto p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              title="Refresh">
              <SafeIcon icon={Lu.LuRefreshCw} size={12} />
            </button>
          </div>

          {loading ? <TableSkeleton rows={4} /> : (
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {activeProfiles.length === 0 && (
                <div className="py-16 text-center space-y-3">
                  <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto">
                    <SafeIcon icon={Lu.LuUsers} size={28} className="text-slate-300" />
                  </div>
                  <p className="text-[10px] font-black uppercase text-slate-400">No staff profiles found</p>
                  <p className="text-[9px] text-slate-300 max-w-xs mx-auto leading-relaxed">
                    Run the migration <code className="bg-slate-100 px-1 rounded">1778800000000-add_created_at_to_profiles.sql</code> in
                    Supabase SQL Editor, then click Retry.
                  </p>
                  <button onClick={fetchAll}
                    className="mt-2 px-4 py-2 bg-primary-600 text-white text-[10px] font-black uppercase rounded-lg hover:bg-primary-700 transition-colors">
                    Retry Fetch
                  </button>
                </div>
              )}

              {activeProfiles.map((p, i) => {
                const roleStyle = ROLE_STYLES[p.role] || ROLE_STYLES.teacher;
                const displayName = p.teacher_nickname || `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email || 'Unknown';
                const lastLogin = auditLogs.find(l => l.user_id === p.id);
                const creatorName = getCreatorName(p.created_by);

                return (
                  <motion.div key={p.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                    className={cn('px-6 py-4 flex items-center gap-4', p.is_disabled && 'opacity-60 bg-slate-50/50 dark:bg-slate-800/30')}>
                    {/* Avatar */}
                    <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm shrink-0',
                      p.is_disabled ? 'bg-slate-200 text-slate-400' : 'bg-primary-100 text-primary-700')}>
                      {displayName?.charAt(0)?.toUpperCase() || '?'}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-0.5">
                        <p className="font-black text-xs uppercase dark:text-white truncate">{displayName}</p>
                        <Badge variant={roleStyle.variant} className="text-[7px] uppercase font-black">{roleStyle.label}</Badge>
                        {p.is_disabled && (
                          <Badge variant="default" className="text-[7px] uppercase font-black flex items-center gap-0.5 bg-slate-200 text-slate-500">
                            <SafeIcon icon={Lu.LuShieldOff} size={8} /> Disabled
                          </Badge>
                        )}
                        {p.is_default_password && !p.is_disabled && (
                          <Badge variant="warning" className="text-[7px] uppercase font-black flex items-center gap-0.5">
                            <SafeIcon icon={Lu.LuShieldAlert} size={8} /> Default PW
                          </Badge>
                        )}
                      </div>
                      <p className="text-[9px] text-slate-400 font-bold">{p.email}</p>
                      {creatorName && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <SafeIcon icon={Lu.LuUserCheck} size={9} className="text-indigo-400" />
                          <p className="text-[8px] text-indigo-400 font-bold uppercase">Created by {creatorName}</p>
                        </div>
                      )}
                      {lastLogin ? (
                        <p className="text-[8px] text-slate-300 font-bold mt-0.5">
                          Last login: {fmtDate(lastLogin.created_at, 'dd MMM yyyy, h:mm a')}
                        </p>
                      ) : (
                        <p className="text-[8px] text-slate-300 font-bold mt-0.5">No logins recorded yet</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                      <select
                        value={p.role}
                        onChange={e => handleUpdateRole(p.id, e.target.value)}
                        className="text-[9px] font-black uppercase bg-slate-50 border rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-primary-500">
                        <option value="teacher">Teacher</option>
                        <option value="executive">Executive</option>
                        <option value="owner">Owner</option>
                      </select>
                      <button onClick={() => setDisableModal({ open: true, profile: p })}
                        title={p.is_disabled ? 'Reinstate account' : 'Temporarily disable account'}
                        className={cn('p-2 rounded-lg transition-colors',
                          p.is_disabled ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' : 'bg-amber-50 text-amber-500 hover:bg-amber-100')}>
                        <SafeIcon icon={p.is_disabled ? Lu.LuShieldCheck : Lu.LuShieldOff} size={13} />
                      </button>
                      <button onClick={() => handleDeleteProfile(p)}
                        className="p-2 bg-rose-50 text-rose-500 hover:bg-rose-100 rounded-lg transition-colors"
                        title="Permanently delete account">
                        <SafeIcon icon={Lu.LuTrash2} size={13} />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Pending Invites Tab ── */}
      {activeTab === 'pending' && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-6 py-4 border-b bg-amber-50 dark:bg-slate-800">
            <SafeIcon icon={Lu.LuClock} size={14} className="text-amber-600" />
            <h2 className="text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-slate-300">
              Pending Invites — {pendingInvites.length} unclaimed
            </h2>
          </div>

          <div className="mx-6 mt-4 bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3">
            <SafeIcon icon={Lu.LuInfo} size={14} className="text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-[10px] font-black uppercase text-blue-700 tracking-widest mb-1">How staff onboarding works</p>
              <ol className="text-[9px] text-blue-700 space-y-0.5 list-decimal pl-3">
                <li>Create an invite — a temporary password is auto-generated</li>
                <li>Share the email + temp password with the teacher</li>
                <li>Teacher signs in using those credentials on the login page</li>
                <li>On first login, their profile is auto-created and they appear in <strong>Staff Accounts</strong></li>
                <li>The invite disappears from this list once claimed</li>
              </ol>
            </div>
          </div>

          {loading ? <TableSkeleton rows={3} /> : (
            <div className="divide-y divide-slate-200 dark:divide-slate-700 mt-4">
              {pendingInvites.length === 0 && (
                <div className="py-12 text-center">
                  <SafeIcon icon={Lu.LuMailCheck} size={32} className="text-slate-200 mx-auto mb-3" />
                  <p className="text-[10px] font-black uppercase text-slate-300">No pending invites</p>
                  <p className="text-[9px] text-slate-300 mt-1">All invites have been claimed</p>
                </div>
              )}

              {pendingInvites.map((inv, i) => {
                const creatorName = getCreatorName(inv.created_by);
                return (
                  <motion.div key={inv.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.04 }} className="px-6 py-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center font-black text-sm shrink-0">
                        {(inv.first_name || inv.email)?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <p className="font-black text-xs uppercase dark:text-white">
                            {`${inv.first_name || ''} ${inv.last_name || ''}`.trim() || inv.email}
                          </p>
                          <Badge variant="warning" className="text-[7px] uppercase font-black">{inv.role}</Badge>
                          <Badge variant="default" className="text-[7px] uppercase font-black">Pending</Badge>
                        </div>
                        <p className="text-[9px] text-slate-400 font-bold">{inv.email}</p>
                        {inv.teacher_nickname && (
                          <p className="text-[8px] text-slate-400 font-bold uppercase">Nickname: {inv.teacher_nickname}</p>
                        )}
                        {creatorName && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <SafeIcon icon={Lu.LuUserCheck} size={9} className="text-indigo-400" />
                            <p className="text-[8px] text-indigo-400 font-bold uppercase">Created by {creatorName}</p>
                          </div>
                        )}
                        <p className="text-[8px] text-slate-300 font-bold mt-0.5">
                          Created: {fmtDate(inv.created_at, 'dd MMM yyyy, h:mm a')}
                        </p>
                        <div className="mt-3 bg-slate-900 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <p className="text-[8px] font-black uppercase text-slate-400">Email</p>
                              <p className="text-[9px] font-mono text-white">{inv.email}</p>
                              <button onClick={() => copyToClipboard(inv.email)} className="p-0.5 text-slate-500 hover:text-white">
                                <SafeIcon icon={Lu.LuCopy} size={9} />
                              </button>
                            </div>
                            <div className="flex items-center gap-2">
                              <p className="text-[8px] font-black uppercase text-slate-400">Password</p>
                              <p className="text-[9px] font-mono text-amber-400">{inv.temp_password}</p>
                              <button onClick={() => copyToClipboard(inv.temp_password)} className="p-0.5 text-slate-500 hover:text-white">
                                <SafeIcon icon={Lu.LuCopy} size={9} />
                              </button>
                            </div>
                          </div>
                          <button
                            onClick={() => copyToClipboard(`Email: ${inv.email}\nPassword: ${inv.temp_password}`)}
                            className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-[9px] font-black uppercase rounded-lg hover:bg-primary-700 transition-colors shrink-0">
                            <SafeIcon icon={Lu.LuCopy} size={11} /> Copy Both
                          </button>
                        </div>
                      </div>
                      <button onClick={() => handleDeletePending(inv)}
                        className="p-2 bg-rose-50 text-rose-500 hover:bg-rose-100 rounded-lg transition-colors shrink-0"
                        title="Cancel invite">
                        <SafeIcon icon={Lu.LuTrash2} size={13} />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Audit Tab ── */}
      {activeTab === 'audit' && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-6 py-4 border-b bg-slate-50 dark:bg-slate-800">
            <SafeIcon icon={Lu.LuActivity} size={14} className="text-emerald-600" />
            <h2 className="text-[10px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-300">
              Login Audit — {filteredLogs.length} events
            </h2>
          </div>

          {loading ? <TableSkeleton rows={5} /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-50 dark:bg-slate-800 text-[8px] font-black uppercase text-slate-400 tracking-widest border-b">
                  <tr>
                    <th className="px-6 py-3">User</th>
                    <th className="px-6 py-3">Action</th>
                    <th className="px-6 py-3">Date & Time</th>
                    <th className="px-6 py-3 hidden md:table-cell">User Agent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {filteredLogs.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-12 text-center text-[10px] font-black uppercase text-slate-300">
                        No login events recorded yet
                      </td>
                    </tr>
                  )}
                  {filteredLogs.map((log, i) => {
                    const prof = profiles.find(p => p.id === log.user_id);
                    const displayName = prof
                      ? (prof.teacher_nickname || `${prof.first_name || ''} ${prof.last_name || ''}`.trim() || log.email)
                      : log.email;
                    return (
                      <motion.tr key={log.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                        className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-3">
                          <p className="font-black text-xs uppercase dark:text-white">{displayName}</p>
                          <p className="text-[9px] text-slate-400 font-bold">{log.email}</p>
                        </td>
                        <td className="px-6 py-3">
                          <Badge variant="success" className="text-[7px] uppercase font-black">
                            <SafeIcon icon={Lu.LuLogIn} size={9} className="mr-1" />
                            {log.action}
                          </Badge>
                        </td>
                        <td className="px-6 py-3">
                          <p className="text-[10px] font-black dark:text-white">{fmtDate(log.created_at, 'dd MMM yyyy')}</p>
                          <p className="text-[9px] text-slate-400 font-bold">{fmtDate(log.created_at, 'h:mm:ss a')}</p>
                        </td>
                        <td className="px-6 py-3 hidden md:table-cell">
                          <p className="text-[8px] text-slate-400 font-medium truncate max-w-[200px]">
                            {log.metadata?.user_agent || '—'}
                          </p>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Disable / Reinstate Modal ── */}
      <Modal
        isOpen={disableModal.open}
        onClose={() => setDisableModal({ open: false, profile: null })}
        title={disableModal.profile?.is_disabled ? 'Reinstate Account' : 'Disable Account'}
        className="max-w-md">
        <DisableModal
          profile={disableModal.profile}
          onClose={() => setDisableModal({ open: false, profile: null })}
          onConfirm={handleToggleDisable}
          saving={disableSaving}
        />
      </Modal>

      {/* ── Create Staff Modal ── */}
      <Modal isOpen={modalOpen} onClose={() => { setModalOpen(false); resetForm(); }} title="Add New Staff Member" className="max-w-lg">
        <form onSubmit={handleCreateTeacher} className="space-y-4 p-1">
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 flex items-start gap-3">
            <SafeIcon icon={Lu.LuShieldCheck} size={14} className="text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-[10px] font-black uppercase text-emerald-700 tracking-widest mb-0.5">Invite-Based Onboarding</p>
              <p className="text-[10px] text-emerald-700 leading-relaxed">
                A secure invite is created instantly. Share the credentials with the teacher — they'll appear in Staff Accounts after their first sign-in.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-2.5">
            <SafeIcon icon={Lu.LuUserCheck} size={13} className="text-indigo-500 shrink-0" />
            <p className="text-[9px] font-black uppercase text-indigo-600 tracking-widest">
              Creating under:{' '}
              <span className="text-indigo-800">
                {ownerProfile?.teacher_nickname || `${ownerProfile?.first_name || ''} ${ownerProfile?.last_name || ''}`.trim() || currentUser?.email}
              </span>
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input label="First Name *" value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))} placeholder="Jane" required />
            <Input label="Last Name" value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))} placeholder="Smith" />
          </div>
          <Input label="Email Address *" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="jane@happynestling.com" required />
          <Input label="Mobile Number" value={form.mobile_number} onChange={e => setForm(p => ({ ...p, mobile_number: e.target.value }))} placeholder="+60 12-345 6789" />
          <Input label="Display Nickname" value={form.teacher_nickname} onChange={e => setForm(p => ({ ...p, teacher_nickname: e.target.value }))} placeholder="Ms. Jane" />

          <div className="space-y-1">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Role</label>
            <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
              className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="teacher">Teacher</option>
              <option value="executive">Executive</option>
              <option value="owner">Owner</option>
            </select>
          </div>

          <div className="bg-slate-900 rounded-xl p-4">
            <p className="text-[8px] font-black uppercase text-slate-400 tracking-widest mb-2">Auto-Generated Credentials</p>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase">Temp Password</p>
                <p className="text-base font-mono font-black text-amber-400">{generatedPassword}</p>
              </div>
              <button type="button" onClick={() => copyToClipboard(generatedPassword)}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-700 text-white text-[9px] font-black uppercase rounded-lg hover:bg-slate-600 transition-colors">
                <SafeIcon icon={Lu.LuCopy} size={11} /> Copy
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => { setModalOpen(false); resetForm(); }} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-[2] font-black uppercase text-[10px] tracking-widest">
              {saving ? 'Creating...' : 'Create Invite'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}